
import { ICOSFormCompliance } from "@/models/COS";
import { validateDate } from "@/utils/dates";
import { TInputRule } from "@nationbetter/nb-components/src/models/Input";
import { computed, defineComponent, getCurrentInstance, onMounted, ref, watch } from "vue-demi";

const DEFAULT_FORM: ICOSFormCompliance = {
  cos_tier: "",
  cos_sponsor_licence_number: "",
  cos_sponsor_name: "",
  cos_number: "",
  cos_issue_date: "",
  cos_expiry_date: "",
  cos_note: "",
  cos_last_name: "",
  cos_first_name: "",
  cos_other_name_n_: "",
  cos_nationality_n_: "",
  place_of_birth: "",
  country_of_birth: "",
  date_of_birth: "",
  gender: "",
  cos_gender: "",
  national_insurance_number: "",
  employee_number: "",
  cos_work_start_date: "",
  cos_work_end_date: "",
  if_to_leave_uk: false,
  hours_work: "",
  job_title: "",
  job_type: "",
  job_description: "",
  if_client_contract: false,
  client_contract_details: "",
  basic_salary: "",
  migrant_salary_for_each: "",
  salary_and_allowances: "",
  allowances: "",
  job_title_shortage_list: false,
  maintenance_status: false,
  registration_details: "",
  atas_required: false,
  phd_required_for_post: false,
  if_passed_rlmt: false,
  work_address: {
    address: "",
    address2: "",
    city: "",
    county: "",
    postcode: "",
  },
  other_work_addresses: [],
};

export default defineComponent({
  name: "FormCOSCompliance",

  props: {
    loading: { type: Boolean, default: false },
    details: { type: Object as () => ICOSFormCompliance, required: true },
  },

  emits: ["update"],

  setup(props, { emit }) {
    const instance = getCurrentInstance()?.proxy;

    const form = ref<ICOSFormCompliance>({ ...DEFAULT_FORM });

    const requiredRules: TInputRule[] = [(v) => !!v || String(instance?.$t("errors.noValue"))];

    const options = [
      { value: true, label: "Yes" },
      { value: false, label: "No" },
    ];

    const optionsYear = ["Contract", "Day", "Hour", "Week", "Month", "Year"].map((label) => ({
      value: label,
      label,
    }));

    const getOtherWorkAddressesHeader = (index: number, items: any[]) => {
      const base =
        instance?.$t("labels.otherWorkAddresses") ?? "Other regular work addresses in the UK";
      if (!items.length || items.length === 1) return base;
      return `${base} [${index + 1}]`;
    };

    const setCosDate = (date: string): string => {
      return date && validateDate(date) ? date : "";
    };

    const toBoolean = (value: boolean | string | undefined): boolean => {
      if (typeof value === "boolean") return value;
      if (typeof value === "string") return value === "true";
      return false;
    };

    const setCosForm = (data: ICOSFormCompliance) => {
      Object.assign(form.value, {
        ...data,
        cos_issue_date: setCosDate(data.cos_issue_date),
        cos_expiry_date: setCosDate(data.cos_expiry_date),
        cos_work_start_date: setCosDate(data.cos_work_start_date),
        cos_work_end_date: setCosDate(data.cos_work_end_date),
        date_of_birth: setCosDate(data.date_of_birth),
        if_to_leave_uk: toBoolean(data.if_to_leave_uk as boolean | string),
        if_client_contract: toBoolean(data.if_client_contract as boolean | string),
        job_title_shortage_list: toBoolean(data.job_title_shortage_list as boolean | string),
        maintenance_status: toBoolean(data.maintenance_status as boolean | string),
        atas_required: toBoolean(data.atas_required as boolean | string),
        phd_required_for_post: toBoolean(data.phd_required_for_post as boolean | string),
        if_passed_rlmt: toBoolean(data.if_passed_rlmt as boolean | string),
        work_address: {
          address: data.work_address?.address || "",
          address2: data.work_address?.address2 || "",
          city: data.work_address?.city || "",
          county: data.work_address?.county || "",
          postcode: data.work_address?.postcode || "",
        },
        gender: data.gender || data.cos_gender || "",
        job_type: data.job_type ? data.job_type.replace(/\n/g, " ").trim() : "",
        migrant_salary_for_each:
          optionsYear.find((option) => option.value === data.migrant_salary_for_each)?.value || "",
      });
    };

    const isFormValid = computed(() => {
      const cos_sponsor_licence_number = form.value.cos_sponsor_licence_number;

      return !!cos_sponsor_licence_number;
    });

    const dropdownKey = ref(0);

    onMounted(() => {
      setCosForm(props.details);

      emit("update", {
        form: form.value,
        valid: isFormValid.value,
      });

      dropdownKey.value++;
    });

    watch(
      () => form.value,
      () => {
        const payload: ICOSFormCompliance = {
          ...form.value,
          maintenance_status: form.value.maintenance_status,
        };

        emit("update", {
          form: payload,
          valid: isFormValid.value,
        });
      },
      { deep: true, immediate: true }
    );

    return {
      form,
      options,
      optionsYear,
      getOtherWorkAddressesHeader,
      isFormValid,
      requiredRules,
      dropdownKey,
    };
  },
});
